import { emailApi, EMAIL_TEMPLATE_KEY } from 'lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PreviewPane from '../components/PreviewPane';
import {
  Btn,
  Card,
  Field,
  Grid,
  Loading,
  Note,
  PageBand,
  SectionHead,
  T,
  TextInput
} from '../components/ui';

/**
 * `/admin/email/banner` — the hero banner's wording, in every language it is written in.
 *
 * ## Why this is a page and not a section of Settings
 *
 * Settings holds the rules a send is evaluated against. This is copy — the two lines a fan reads
 * first, in two languages, beside a rendering of each. Putting it under Settings would bury it
 * behind a heading about fatigue caps, and the preview pane's affordance needs somewhere to point
 * that opens *on* the thing being edited rather than scrolled to it.
 *
 * ## Why both languages are on screen at once
 *
 * A translation is judged against its original, not on its own. Stacking them in one column each
 * means an operator reads the English line and the Japanese line as a pair, which is the only way
 * to notice that one of them says something the other does not.
 *
 * ## An empty box is not an error
 *
 * This is the part most likely to be "fixed" later by someone adding a required validator. Every
 * field except the fallback language's headline may be left empty, and empty means *this line has
 * no translation yet* — the email then uses the fallback wording for that line alone. Refusing to
 * save an empty Japanese headline would make it impossible to say "we have translated the subline
 * and not the headline", which is a real and common state partway through a translation.
 *
 * The fallback headline is the exception, because it is what everything else falls back to. Empty
 * there is a banner with no words in it, so the server refuses it and so does the button below.
 */

/**
 * The template rendered for this page's preview. It carries the banner and is the one every new fan
 * gets; the preview shows its banner alone (`bannerOnly`), not the welcome email around it.
 */
const PREVIEW_TEMPLATE = EMAIL_TEMPLATE_KEY?.WELCOME ?? 'welcome';

/** Matches the settle the composer's preview already uses, so the two screens feel the same. */
const SETTLE_MS = 350;

const LANGUAGE_NAMES = {
  en: 'English',
  ja: 'Japanese'
};

const emptyCopy = () => ({ headline: '', subline: '' });

/*
  The longest wording that stays inside the banner's text area, per language.

  The server's own figures (`BANNER_COPY_LIMITS`) arrive with the wording and win; these are the
  same numbers, for a server that predates them. Past them the words wrap beyond two lines in the
  left 57% of the banner that is theirs, and the photograph — scaled to the taller banner — brings
  the player into them. That is the QA report this page now prevents.
*/
const DEFAULT_LIMITS = {
  en: { headline: 55, subline: 100 },
  ja: { headline: 30, subline: 55 }
};

const FIELD_NAMES = { headline: 'headline', subline: 'supporting line' };

const Banner = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(null);
  const [locales, setLocales] = useState([]);
  const [fallbackLocale, setFallbackLocale] = useState('en');
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [copy, setCopy] = useState({});
  const [savedCopy, setSavedCopy] = useState({});
  const [version, setVersion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const [previews, setPreviews] = useState({});
  const [previewLoading, setPreviewLoading] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(null);

    const response = await emailApi.getBannerCopy();
    const data = response?.data;

    if (!data) {
      setFailed('The banner wording could not be loaded.');
      setLoading(false);
      return;
    }

    setLocales(data.locales ?? []);
    setFallbackLocale(data.fallbackLocale ?? 'en');
    setLimits({ ...DEFAULT_LIMITS, ...(data.limits ?? {}) });
    setCopy(data.bannerCopy ?? {});
    setSavedCopy(data.bannerCopy ?? {});
    setVersion(data.version ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /*
    Dirty is computed from the two snapshots rather than set by every keystroke.

    A flag set on change and cleared on save gets out of step the moment someone types a character
    and deletes it again — they are then warned about losing edits they no longer have.
  */
  const dirty = useMemo(
    () => JSON.stringify(copy) !== JSON.stringify(savedCopy),
    [copy, savedCopy]
  );

  /*
    The browser's own warning, which is the only one that can stop a tab close or a back button.

    A React-router prompt covers in-app navigation and nothing else, and the way this page is
    reached — a link from the composer — makes the browser back button the most likely way out.
  */
  useEffect(() => {
    if (!dirty) return undefined;

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const setField = (locale, field, value) => {
    setOutcome(null);
    setCopy((current) => ({
      ...current,
      [locale]: { ...emptyCopy(), ...current[locale], [field]: value }
    }));
  };

  /*
    One preview per language, each following its own fields.

    Rendered through `previewTemplate` with a locale rather than through anything banner-specific,
    so this page and a real send resolve the wording by the same path. That includes the fallback:
    an untranslated line previews as the fallback wording, because that is what would arrive.
  */
  const settleTimers = useRef({});

  useEffect(() => {
    if (!locales.length) return undefined;

    locales.forEach((locale) => {
      clearTimeout(settleTimers.current[locale]);

      settleTimers.current[locale] = setTimeout(async () => {
        setPreviewLoading((current) => ({ ...current, [locale]: true }));

        const response = await emailApi.previewTemplate(
          PREVIEW_TEMPLATE,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          locale
        );

        setPreviews((current) => ({ ...current, [locale]: response?.data }));
        setPreviewLoading((current) => ({ ...current, [locale]: false }));
      }, SETTLE_MS);
    });

    const timers = settleTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, [locales, savedCopy]);

  const fallbackHeadline = copy[fallbackLocale]?.headline?.trim();

  // Measured as the server measures: trimmed, in characters.
  const lengthOf = (locale, field) => (copy[locale]?.[field] ?? '').trim().length;
  const limitOf = (locale, field) => limits[locale]?.[field] ?? Infinity;

  /*
    Wording too long for the banner, named field by field.

    Not a hard `maxLength`: that cuts a pasted sentence off mid-word without saying so, and cannot
    help with wording saved before these limits existed. The box takes what is typed, counts it, and
    the Save button says what to shorten.
  */
  const tooLong = locales.flatMap((locale) =>
    ['headline', 'subline']
      .filter((field) => lengthOf(locale, field) > limitOf(locale, field))
      .map((field) => ({ locale, field }))
  );

  const tooLongMessage = tooLong.length
    ? `Too long for the banner: ${tooLong
        .map(
          ({ locale, field }) =>
            `the ${LANGUAGE_NAMES[locale] ?? locale.toUpperCase()} ${FIELD_NAMES[field]}`
        )
        .join(', ')}. Shorten ${tooLong.length === 1 ? 'it' : 'them'} to save.`
    : null;

  const counter = (locale, field) =>
    `${lengthOf(locale, field)} / ${limitOf(locale, field)} characters`;

  const fieldError = (locale, field) => {
    const over = lengthOf(locale, field) - limitOf(locale, field);
    if (over > 0) {
      return `${over} ${over === 1 ? 'character' : 'characters'} too long — the banner fits ${limitOf(
        locale,
        field
      )}, so the words stay clear of the player.`;
    }
    if (field === 'headline' && locale === fallbackLocale && !fallbackHeadline) {
      return 'Enter a headline. Every other language falls back to this one.';
    }
    return undefined;
  };

  const save = async () => {
    setSaving(true);
    setOutcome(null);

    const response = await emailApi.updateBannerCopy({ bannerCopy: copy, version });

    setSaving(false);

    if (!response?.data) {
      /*
        The typing is left exactly as it was. A failed save that also cleared the boxes would cost
        an operator the paragraph they just wrote, which is a worse outcome than the failure.
      */
      setOutcome({
        tone: 'bad',
        text:
          response?.message ??
          'That did not save. Your wording is still here — try again, or reload if someone else has been editing.'
      });
      return;
    }

    setSavedCopy(response.data.bannerCopy ?? copy);
    setCopy(response.data.bannerCopy ?? copy);
    setVersion(response.data.version ?? null);
    setOutcome({ tone: 'good', text: 'Saved. The next email rendered uses this wording.' });
  };

  if (loading) return <Loading title="Loading the banner wording…" />;

  return (
    <>
      <PageBand
        brand
        title="Banner"
        subtitle="The two lines at the top of every welcome and campaign email, in each language"
        actions={
          <Btn variant="outline" onClick={() => navigate('/admin/email/setup')}>
            Back to setup
          </Btn>
        }
      />

      {failed ? (
        <Card>
          <Note>{failed}</Note>
        </Card>
      ) : null}

      <Card>
        <SectionHead
          icon="Pencil"
          title="Banner wording"
          right={
            <Btn
              icon="Check"
              onClick={save}
              disabled={saving || !dirty || !fallbackHeadline || tooLong.length > 0}
              title={
                tooLongMessage ??
                (fallbackHeadline
                  ? undefined
                  : `The ${LANGUAGE_NAMES[fallbackLocale] ?? fallbackLocale} headline cannot be empty`)
              }
            >
              {saving ? 'Saving…' : 'Save wording'}
            </Btn>
          }
        />

        {tooLongMessage && dirty ? (
          <Note role="alert" style={{ color: T.red }}>
            {tooLongMessage}
          </Note>
        ) : null}

        {outcome ? (
          <Note
            role="status"
            style={{ color: outcome.tone === 'bad' ? (T.danger ?? T.muted) : T.muted }}
          >
            {outcome.text}
          </Note>
        ) : null}

        <Grid>
          {locales.map((locale) => {
            const entry = copy[locale] ?? emptyCopy();
            const isFallback = locale === fallbackLocale;
            const name = LANGUAGE_NAMES[locale] ?? locale.toUpperCase();

            return (
              <div key={locale} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <SectionHead icon="Mail" title={name} />

                <Field
                  label="Headline"
                  required={isFallback}
                  error={fieldError(locale, 'headline')}
                  hint={`${counter(locale, 'headline')}${
                    isFallback
                      ? ' · Every other language falls back to this line, so it cannot be empty.'
                      : entry.headline.trim()
                        ? ''
                        : ` · Empty — ${name} readers will see the ${
                            LANGUAGE_NAMES[fallbackLocale] ?? fallbackLocale
                          } headline.`
                  }`}
                >
                  <TextInput
                    value={entry.headline}
                    onChange={(event) => setField(locale, 'headline', event.target.value)}
                  />
                </Field>

                <Field
                  label="Supporting line"
                  error={fieldError(locale, 'subline')}
                  hint={`${counter(locale, 'subline')}${
                    entry.subline.trim()
                      ? ''
                      : ` · Empty — ${name} readers will see the ${
                          LANGUAGE_NAMES[fallbackLocale] ?? fallbackLocale
                        } supporting line.`
                  }`}
                >
                  <TextInput
                    value={entry.subline}
                    onChange={(event) => setField(locale, 'subline', event.target.value)}
                  />
                </Field>

                <PreviewPane
                  compact
                  bannerOnly
                  mobile={previews[locale]?.mobile}
                  desktop={previews[locale]?.desktop}
                  loading={Boolean(previewLoading[locale])}
                />
              </div>
            );
          })}
        </Grid>

        <Note>
          Only these two lines change with the reader&apos;s language. The wordmark, the App Store
          and Google Play buttons, &ldquo;follow us&rdquo; and the handle stay in English in every
          language — that is how the design draws them.
        </Note>
      </Card>
    </>
  );
};

export default Banner;
